import { useCallback, useEffect, useMemo, useState } from 'react'
import { Route, Routes } from 'react-router-dom'

import type { RunResult } from '@/game/useGame'
import { getLang } from '@/i18n'
import { flushLeadQueue } from '@/lib/api'
import { makeCode } from '@/lib/code'
import {
  personalBestToday,
  saveScore,
  topToday as readTopToday,
  type ScoreEntry,
} from '@/lib/leaderboard'
import { readJson, writeJson } from '@/lib/storage'
import GameOverScreen from '@/screens/GameOver'
import PlayScreen from '@/screens/Play'
import StartScreen from '@/screens/Start'
import VerifyScreen from '@/screens/Verify'

type Phase = 'start' | 'play' | 'over'

interface Summary {
  result: RunResult
  code: string
  rank: number
  isPersonalBest: boolean
}

const MUTED_KEY = 'muted.v1'
const NAME_KEY = 'lastName.v1'

function Game() {
  const [phase, setPhase] = useState<Phase>('start')
  const [name, setName] = useState(() => readJson<string>(NAME_KEY, ''))
  // Muted by default: a trade-fair hall is loud and nobody wants a phone
  // shouting in a queue. The unmute button is always visible.
  const [muted, setMuted] = useState(() => readJson<boolean>(MUTED_KEY, true))
  const [summary, setSummary] = useState<Summary | null>(null)
  const [board, setBoard] = useState<ScoreEntry[]>(() => readTopToday())

  useEffect(() => {
    void flushLeadQueue()
    const retry = () => void flushLeadQueue()
    window.addEventListener('online', retry)
    return () => window.removeEventListener('online', retry)
  }, [])

  const toggleMute = useCallback(() => {
    setMuted((current) => {
      writeJson(MUTED_KEY, !current)
      return !current
    })
  }, [])

  const handleStart = useCallback((playerName: string) => {
    setName(playerName)
    writeJson(NAME_KEY, playerName)
    setSummary(null)
    setPhase('play')
  }, [])

  const handleCrash = useCallback(
    (result: RunResult) => {
      const previousBest = personalBestToday(name)
      const code = makeCode(result.score)
      const rank = saveScore({
        name,
        score: result.score,
        ts: Date.now(),
        code,
        stats: result.stats,
      })
      setBoard(readTopToday())
      setSummary({
        result,
        code,
        rank,
        isPersonalBest: previousBest === null || result.score > previousBest,
      })
      setPhase('over')
    },
    [name],
  )

  const restart = useCallback(() => {
    setSummary(null)
    setPhase('play')
  }, [])

  const goHome = useCallback(() => {
    setSummary(null)
    setPhase('start')
  }, [])

  // A fresh key remounts the loop, so "play again" always starts a clean run.
  const runKey = useMemo(() => `${phase}-${Date.now()}`, [phase])

  if (phase === 'play') {
    return (
      <PlayScreen
        key={runKey}
        name={name}
        muted={muted}
        onToggleMute={toggleMute}
        onCrash={handleCrash}
      />
    )
  }

  if (phase === 'over' && summary) {
    return (
      <GameOverScreen
        name={name}
        score={summary.result.score}
        code={summary.code}
        stats={summary.result.stats}
        crashKind={summary.result.crashKind}
        rank={summary.rank}
        isPersonalBest={summary.isPersonalBest}
        topToday={board}
        onRestart={restart}
        onHome={goHome}
      />
    )
  }

  return (
    <StartScreen
      onStart={handleStart}
      lastName={name}
      topToday={board}
      muted={muted}
      onToggleMute={toggleMute}
    />
  )
}

export default function App() {
  useEffect(() => {
    document.documentElement.lang = getLang()
  }, [])

  return (
    <Routes>
      <Route
        path='/'
        element={<Game />}
      />
      <Route
        path='/weryfikacja'
        element={<VerifyScreen />}
      />
      <Route
        path='*'
        element={<Game />}
      />
    </Routes>
  )
}
